import {View, Text, StyleSheet, Modal, TouchableOpacity, Image} from 'react-native';
import {useTranslation} from 'react-i18next';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {useCurrentTheme} from "@/theming/useCurrentTheme";
import {ThemeType} from "@/theming/baseTheme";

// Payment provider logos as local assets
const paymentProviderLogos = {
    stripe: require('@/assets/images/payment-providers/stripe.png'),
    paypal: require('@/assets/images/payment-providers/paypal.png')
};

// Helper function to get payment provider info
const getPaymentProviderInfo = (providerId: string, t: any) => {
    const providers = {
        stripe: {
            name: t('offers.paymentProviders.stripe.name'),
            description: t('offers.paymentProviders.stripe.description'),
            color: '#635BFF'
        },
        paypal: {
            name: t('offers.paymentProviders.paypal.name'),
            description: t('offers.paymentProviders.paypal.description'),
            color: '#003087'
        }
    };

    return providers[providerId as keyof typeof providers] || {
        name: providerId,
        description: 'Payment Provider',
        color: '#000000'
    };
};

interface PaymentProviderModalProps {
    visible: boolean;
    availableProviders: string[];
    isProcessing: boolean;
    onSelectProvider: (providerId: string) => void;
    onClose: () => void;
}

export const PaymentProviderModal = ({
    visible,
    availableProviders,
    isProcessing,
    onSelectProvider,
    onClose
}: PaymentProviderModalProps) => {
    const {t} = useTranslation();
    const {theme} = useCurrentTheme();
    const insets = useSafeAreaInsets();
    const styles = createStyles(theme, insets);

    return (
        <Modal
            visible={visible}
            transparent={true}
            animationType="slide"
            onRequestClose={onClose}
        >
            <TouchableOpacity
                style={styles.modalOverlay}
                activeOpacity={1}
                onPress={onClose}
            >
                <TouchableOpacity activeOpacity={1}>
                    <View style={styles.modalContent}>
                        <View style={styles.modalHeader}>
                            <Text style={styles.modalTitle}>{t('offers.paymentProviders.selectTitle')}</Text>
                            <Text style={styles.modalSubtitle}>{t('offers.paymentProviders.selectSubtitle')}</Text>
                        </View>

                        <View style={styles.providersList}>
                            {availableProviders.map(providerId => {
                                const providerInfo = getPaymentProviderInfo(providerId, t);
                                return (
                                    <TouchableOpacity
                                        key={providerId}
                                        style={styles.providerOption}
                                        onPress={() => onSelectProvider(providerId)}
                                        disabled={isProcessing}
                                    >
                                        <View style={styles.providerContent}>
                                            <View style={[styles.providerIcon, {backgroundColor: '#F0F2F5'}]}>
                                                <Image
                                                    source={paymentProviderLogos[providerId.toLowerCase() as keyof typeof paymentProviderLogos] || paymentProviderLogos.stripe}
                                                    style={styles[`${providerId.toLowerCase()}Logo` as keyof typeof styles] as typeof styles.providerLogo || styles.providerLogo}
                                                    resizeMode="contain"
                                                />
                                            </View>
                                            <View style={styles.providerDetails}>
                                                <Text style={styles.providerName}>
                                                    Pay with {providerInfo.name}
                                                </Text>
                                                <Text style={styles.providerDescription}>
                                                    {providerInfo.description}
                                                </Text>
                                            </View>
                                            <Text style={styles.chevron}>›</Text>
                                        </View>
                                    </TouchableOpacity>
                                );
                            })}
                        </View>

                        <TouchableOpacity
                            style={styles.cancelButton}
                            onPress={onClose}
                        >
                            <Text style={styles.cancelButtonText}>{t('common.cancel')}</Text>
                        </TouchableOpacity>
                    </View>
                </TouchableOpacity>
            </TouchableOpacity>
        </Modal>
    );
};

const createStyles = (theme: ThemeType, insets: any) => StyleSheet.create({
    modalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        justifyContent: 'flex-end',
    },
    modalContent: {
        backgroundColor: theme.colors.background,
        borderTopLeftRadius: 24,
        borderTopRightRadius: 24,
        paddingTop: 12,
        paddingBottom: insets.bottom + 20,
    },
    modalHeader: {
        paddingHorizontal: 24,
        paddingVertical: 20,
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.border,
        marginBottom: 8,
    },
    modalTitle: {
        fontSize: 22,
        fontWeight: 'bold',
        color: theme.colors.text,
        marginBottom: 4,
    },
    modalSubtitle: {
        fontSize: 14,
        color: theme.colors.textSecondary,
    },
    providersList: {
        paddingHorizontal: 20,
        paddingVertical: 8,
    },
    providerOption: {
        backgroundColor: theme.colors.surface,
        borderRadius: 12,
        marginBottom: 12,
        borderWidth: 1,
        borderColor: theme.colors.border,
        overflow: 'hidden',
    },
    providerContent: {
        flexDirection: 'row',
        alignItems: 'center',
        padding: 16,
    },
    providerIcon: {
        width: 48,
        height: 48,
        borderRadius: 8,
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: 16,
        overflow: 'hidden',
    },
    providerLogo: {
        width: '100%',
        height: '100%',
    },
    stripeLogo: {
        width: '100%',
        height: '100%',
    },
    paypalLogo: {
        width: 32,
        height: 32,
    },
    providerDetails: {
        flex: 1,
    },
    providerName: {
        fontSize: 16,
        color: theme.colors.text,
        fontWeight: '600',
        marginBottom: 4,
    },
    providerDescription: {
        fontSize: 13,
        color: theme.colors.textSecondary,
        lineHeight: 18,
    },
    chevron: {
        fontSize: 28,
        color: theme.colors.textSecondary,
        marginLeft: 8,
    },
    cancelButton: {
        padding: 20,
        marginTop: 8,
        borderTopWidth: 1,
        borderTopColor: theme.colors.border,
    },
    cancelButtonText: {
        fontSize: 16,
        color: theme.colors.textSecondary,
        textAlign: 'center',
        fontWeight: '500',
    },
});
